import { useState, useEffect, useCallback, useRef } from "react";
import { notification } from "antd";
import type { FmvRecord } from "../../components/fmv/FmvTableComponent";
import type {
  FetchVehiclesParams,
  VehiclesMeta,
  BulkUpsertResponse,
} from "../../helpers/interfaces/VehiclesInterfaces";
import {
  fetchVehicles as apiFetchVehicles,
  createVehicle as apiCreateVehicle,
  updateVehicle as apiUpdateVehicle,
  bulkUpsertVehicles as apiBulkUpsert,
} from "../../api/VehiclesApi";
import {
  vehiclesToFmvRecords,
  fmvRecordToPayload,
  fmvRecordsToBulkPayload,
} from "../../helpers/transformers/vehicleTransformer";

const DEFAULT_PARAMS: FetchVehiclesParams = {
  page: 1,
  per_page: 15,
  sort_by: "vehicle_id",
  sort_order: "desc",
};

export const useVehicles = (initialParams?: FetchVehiclesParams) => {
  const [vehicles, setVehicles] = useState<FmvRecord[]>([]);
  const [meta, setMeta] = useState<VehiclesMeta | null>(null);
  const [loading, setLoading] = useState(false);

  const paramsRef = useRef<FetchVehiclesParams>({
    ...DEFAULT_PARAMS,
    ...initialParams,
  });

  // ── Fetch (paginated) ──
  const fetchVehicles = useCallback(
    async (params?: FetchVehiclesParams) => {
      if (params) {
        paramsRef.current = { ...paramsRef.current, ...params };
      }
      setLoading(true);
      try {
        const response = await apiFetchVehicles(paramsRef.current);

        if (response?.data?.data) {
          setVehicles(vehiclesToFmvRecords(response.data.data));
          setMeta(response.data.meta ?? null);
        } else if (response?.status && response.status >= 400) {
          notification.error({
            message:
              response.data?.message ||
              response.data?.error ||
              "Failed to fetch vehicles.",
            duration: 3,
          });
        }
      } catch {
        notification.error({
          message: "Network error while fetching vehicles.",
          duration: 3,
        });
      } finally {
        setLoading(false);
      }
    },
    []
  );

  // ── Create ──
  const createVehicle = useCallback(
    async (record: FmvRecord): Promise<boolean> => {
      setLoading(true);
      try {
        const payload = fmvRecordToPayload(record);
        const response = await apiCreateVehicle(payload);

        if (response?.status === 201 || response?.status === 200) {
          notification.success({
            message: response.data?.message || "Vehicle created successfully.",
            duration: 2,
          });
          setTimeout(() => fetchVehicles({ _t: Date.now() }), 500); // refresh list with delay & cache buster
          return true;
        } else {
          notification.error({
            message:
              response?.data?.message ||
              response?.data?.error ||
              "Failed to create vehicle.",
            duration: 3,
          });
          return false;
        }
      } catch {
        notification.error({
          message: "Network error while creating vehicle.",
          duration: 3,
        });
        return false;
      } finally {
        setLoading(false);
      }
    },
    [fetchVehicles]
  );

  // ── Update ──
  const updateVehicle = useCallback(
    async (vehicleId: number, record: FmvRecord): Promise<boolean> => {
      setLoading(true);
      try {
        const payload = fmvRecordToPayload(record);
        const response = await apiUpdateVehicle(vehicleId, payload);

        if (response?.status === 200) {
          notification.success({
            message: response.data?.message || "Vehicle updated successfully.",
            duration: 2,
          });
          setTimeout(() => fetchVehicles({ _t: Date.now() }), 500); // refresh list with delay & cache buster
          return true;
        } else {
          notification.error({
            message:
              response?.data?.message ||
              response?.data?.error ||
              "Failed to update vehicle.",
            duration: 3,
          });
          return false;
        }
      } catch {
        notification.error({
          message: "Network error while updating vehicle.",
          duration: 3,
        });
        return false;
      } finally {
        setLoading(false);
      }
    },
    [fetchVehicles]
  );

  // ── Bulk Upsert ──
  const bulkUpsert = useCallback(
    async (records: FmvRecord[]): Promise<BulkUpsertResponse | null> => {
      setLoading(true);
      try {
        const payload = fmvRecordsToBulkPayload(records);
        const response = await apiBulkUpsert(payload);

        if (response?.status === 200 || response?.status === 201) {
          const data: BulkUpsertResponse = response.data;
          notification.success({
            message: `Bulk upsert completed — ${data.created} created, ${data.updated} updated, ${data.skipped} skipped.`,
            duration: 4,
          });
          setTimeout(() => fetchVehicles({ _t: Date.now() }), 500); // refresh list with delay & cache buster
          return data;
        } else {
          notification.error({
            message:
              response?.data?.message ||
              response?.data?.error ||
              "Bulk upsert failed.",
            duration: 3,
          });
          return null;
        }
      } catch {
        notification.error({
          message: "Network error during bulk upsert.",
          duration: 3,
        });
        return null;
      } finally {
        setLoading(false);
      }
    },
    [fetchVehicles]
  );

  // ── Convenience refetch ──
  const refetch = useCallback(() => fetchVehicles(), [fetchVehicles]);

  // ── Auto-fetch on mount ──
  useEffect(() => {
    fetchVehicles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    vehicles,
    meta,
    loading,
    fetchVehicles,
    createVehicle,
    updateVehicle,
    bulkUpsert,
    refetch,
  };
};
